import { AgentFile, AgentFileInvalid } from '../aiAgentsConsts';
import { colors, Typography } from '@sweep-io/sweep-design';
import { Box, styled, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUserById } from '../../../reducers/accountUsersReducer';
import { humanizeDate, humanizeDateVariants } from '../../helpers/humanizeDate';
import { AvatarPicture } from '../../avatar/AvatarPicture';
import { DIMENSION_AVATAR_TINY } from '../../../constants';
import { getIsUserInactive } from '../../../lib/getIsUserActive';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import FileDottedIcon from './fileDotted.svg?react';
import FileIcon from './file.svg?react';
import FileListItemLayout from './FileListItemLayout';
import { Warning } from '@sweep-io/sweep-design/dist/icons';
import { ReactNode } from 'react';

const CenteredBox = styled(Box)({
  width: '48px',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
});

const FileIconBox = ({
  backgroundColor,
  strokeColor,
  children,
}: {
  backgroundColor: string;
  strokeColor?: string;
  children: ReactNode;
}) => (
  <CenteredBox
    sx={{
      backgroundColor,
      ...(strokeColor && {
        '& svg path': {
          stroke: `${strokeColor} !important`,
        },
      }),
    }}
  >
    {children}
  </CenteredBox>
);

const FileDescriptionAvatar = ({ userId, opacity = 1 }: { userId: string; opacity?: number }) => {
  const user = useSelector(selectUserById(userId));
  if (!user) return null;

  return (
    <Box sx={{ opacity }}>
      <AvatarPicture
        avatar={{ emoji: user.emoji, imageUrl: user.imageUrl }}
        dimension={DIMENSION_AVATAR_TINY}
        clickable={false}
        isInactive={getIsUserInactive(user.status)}
      />
    </Box>
  );
};

const FileDescription = ({
  file,
  textColor,
  opacity = 1,
}: {
  file: AgentFile;
  textColor: string;
  opacity?: number;
}) => {
  const user = useSelector(selectUserById(file.createdById));
  const descriptionText = `${file.type} file • Uploaded on ${humanizeDate({
    dateOrTimestamp: file.createdAt,
    variant: humanizeDateVariants.SHORT_WITHOUT_TIME,
  })}${user?.name ? ` by ${user?.name}` : ''}`;

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Typography variant="caption" color={textColor}>
        {descriptionText}
      </Typography>
      <FileDescriptionAvatar userId={file.createdById} opacity={opacity} />
    </Box>
  );
};

export const InvalidFileListItem = ({
  file,
  onRemove,
}: {
  file: AgentFileInvalid;
  onRemove: () => void;
}) => {
  const icon = (
    <FileIconBox backgroundColor={colors.blush[300]}>
      <FileDottedIcon />
    </FileIconBox>
  );
  const name = <Typography variant="body-medium">{file.name}</Typography>;
  const description = (
    <Typography variant="caption" color={colors.blush[600]}>
      {file.message}
    </Typography>
  );

  return (
    <FileListItemLayout icon={icon} name={name} description={description} onRemove={onRemove} />
  );
};

export const CompletedFileListItem = ({
  file,
  onRemove,
}: {
  file: AgentFile;
  onRemove: () => void;
}) => {
  const icon = (
    <FileIconBox backgroundColor={colors.blue[100]}>
      <FileIcon />
    </FileIconBox>
  );
  const name = <Typography variant="body-medium">{file.name}</Typography>;
  const description = <FileDescription file={file} textColor={colors.grey[800]} />;

  return (
    <FileListItemLayout icon={icon} name={name} description={description} onRemove={onRemove} />
  );
};

export const ProcessingFileListItem = ({
  file,
  onRemove,
}: {
  file: AgentFile;
  onRemove: () => void;
}) => {
  const icon = (
    <FileIconBox backgroundColor={colors.grey[100]} strokeColor={colors.grey[500]}>
      <FileIcon />
    </FileIconBox>
  );

  const name = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography variant="body-medium" color={colors.grey[500]}>
        {file.name}
      </Typography>
      <CenteredCircularProgress size={16} />
      <Typography variant="caption" color={colors.grey[900]}>
        Processing
      </Typography>
    </Stack>
  );

  const description = <FileDescription file={file} textColor={colors.grey[500]} opacity={0.5} />;

  return (
    <FileListItemLayout icon={icon} name={name} description={description} onRemove={onRemove} />
  );
};

export const FailedFileListItem = ({
  file,
  onRemove,
}: {
  file: AgentFile;
  onRemove: () => void;
}) => {
  const icon = (
    <FileIconBox backgroundColor={colors.grey[100]} strokeColor={colors.grey[500]}>
      <FileIcon />
    </FileIconBox>
  );

  // const handleRetry = () => {
  //   TODO
  // };

  const name = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography variant="body-medium" color={colors.grey[500]}>
        {file.name}
      </Typography>
      <Box
        sx={{
          backgroundColor: colors.blush[100],
          borderRadius: '4px',
          padding: '2px 8px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Warning size="small" color={colors.grey[700]} />
        <Typography variant="caption-bold" color={colors.grey[700]}>
          Upload Failed
        </Typography>
      </Box>
      {/* <Button variant="flat-grey" size="tiny" startIconName="Redo" underlined onClick={handleRetry}>
        Re-try
      </Button> */}
    </Stack>
  );

  const description = <FileDescription file={file} textColor={colors.grey[500]} opacity={0.5} />;

  return (
    <FileListItemLayout icon={icon} name={name} description={description} onRemove={onRemove} />
  );
};
